import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import Toast, { type ToastProps } from './Toast';

interface ToastContextType {
  showToast: (toast: Omit<ToastProps, 'id' | 'onRemove'>) => string;
  removeToast: (id: string) => void;
  success: (title: string, message?: string, options?: ToastOptions) => string;
  error: (title: string, message?: string, options?: ToastOptions) => string;
  warning: (title: string, message?: string, options?: ToastOptions) => string;
  info: (title: string, message?: string, options?: ToastOptions) => string;
  clear: () => void;
}

interface ToastOptions {
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastData extends Omit<ToastProps, 'onRemove'> {
  id: string;
}

interface ToastProviderProps {
  children: ReactNode;
  maxToasts?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

let toastIdCounter = 0;

export const ToastProvider = ({ 
  children, 
  maxToasts = 5,
  position = 'top-right' 
}: ToastProviderProps) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const generateId = useCallback(() => {
    return `toast-${++toastIdCounter}`;
  }, []);

  const showToast = useCallback((toast: Omit<ToastProps, 'id' | 'onRemove'>): string => {
    const id = generateId();
    
    setToasts(prev => {
      const newToasts = [{ ...toast, id }, ...prev];
      // Limitar o número máximo de toasts
      return newToasts.slice(0, maxToasts);
    });

    return id;
  }, [generateId, maxToasts]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const success = useCallback((title: string, message?: string, options?: ToastOptions): string => {
    return showToast({
      type: 'success',
      title,
      message,
      duration: options?.duration,
      action: options?.action
    });
  }, [showToast]);

  const error = useCallback((title: string, message?: string, options?: ToastOptions): string => {
    return showToast({
      type: 'error',
      title,
      message,
      duration: options?.duration || 0, // Erros não desaparecem automaticamente
      action: options?.action
    });
  }, [showToast]);

  const warning = useCallback((title: string, message?: string, options?: ToastOptions): string => {
    return showToast({
      type: 'warning',
      title,
      message,
      duration: options?.duration || 7000, // Warnings ficam mais tempo
      action: options?.action
    });
  }, [showToast]);

  const info = useCallback((title: string, message?: string, options?: ToastOptions): string => {
    return showToast({
      type: 'info',
      title,
      message,
      duration: options?.duration,
      action: options?.action
    });
  }, [showToast]);

  const clear = useCallback(() => {
    setToasts([]);
  }, []);

  const value: ToastContextType = {
    showToast,
    removeToast,
    success,
    error,
    warning,
    info,
    clear
  };

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
    'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2'
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      
      {/* Container de Toasts */}
      <div 
        aria-live="assertive" 
        className={`pointer-events-none fixed z-50 flex flex-col space-y-4 ${positionClasses[position]}`}
        style={{ maxWidth: '420px' }}
      >
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            {...toast}
            onRemove={removeToast}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};